import { useCallback, useLayoutEffect, useRef, useState } from "react";

/** Deep enough to fade most of a row, so a cut-off row reads as more to scroll to. */
const FADE = "2.5rem";

/** A mask fading the list's edges that have rows scrolled out of view. */
const edgeFade = (above: boolean, below: boolean) =>
  above || below
    ? `linear-gradient(to bottom, ${above ? "transparent" : "black"}, black ${FADE}, black calc(100% - ${FADE}), ${below ? "transparent" : "black"})`
    : undefined;

/**
 * Expand inside the existing list viewport so the surrounding card does not jump. Overlay
 * scrollbars stay hidden until used, so the list fades at an edge while rows lie beyond it.
 */
export function useExpandableList() {
  const listRef = useRef<HTMLUListElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [height, setHeight] = useState<number>();
  const [hidden, setHidden] = useState({ above: false, below: false });
  const measure = useCallback(() => {
    const list = listRef.current;
    if (!list) return;
    setHidden({
      above: list.scrollTop > 0,
      below: Math.ceil(list.scrollTop + list.clientHeight) < list.scrollHeight,
    });
  }, []);
  useLayoutEffect(() => {
    const list = listRef.current;
    if (!expanded || !list) return;
    measure();
    // Rows reflow at another width, which can bring them all into view or push some out.
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(list);
    return () => observer.disconnect();
  }, [expanded, measure]);
  const changeExpanded = (next: boolean) => {
    if (next && !expanded) setHeight(listRef.current?.getBoundingClientRect().height);
    if (!next) listRef.current?.scrollTo?.({ top: 0 });
    setExpanded(next);
  };
  return {
    expanded,
    changeExpanded,
    listProps: {
      ref: listRef,
      onScroll: measure,
      style: expanded ? { height, maskImage: edgeFade(hidden.above, hidden.below) } : undefined,
    },
  };
}
